import { Link } from 'react-router-dom'
import { usePeptides } from '../context/PeptidesContext.jsx'
import { CHART_SLUGS } from '../lib/dosingChart.js'
import { faqLd } from '../lib/faq.js'
import FaqBlock from '../components/FaqBlock.jsx'
import Seo from '../components/Seo.jsx'
import LastUpdated from '../components/LastUpdated.jsx'

const CHART_FAQ = [
  { q: 'What is a peptide dosing chart?', a: 'A dosing chart is a quick-reference table that shows how many insulin-syringe units to draw for a given peptide, based on how much bacteriostatic water you add to the vial. Each row is a water amount, each column a target dose, and the cell is the units to draw on a U-100 syringe — the same math our calculator does, laid out so you can read it at a glance.' },
  { q: 'How do I read the units in a chart?', a: 'Find the row for the amount of bacteriostatic water you added, then the column for your target dose. The cell where they meet is the number of units to draw on a U-100 (100 units = 1 mL) insulin syringe. More water means a more dilute solution and more units per dose; less water means fewer, more concentrated units.' },
  { q: 'Do the charts tell me how much to take?', a: 'No. The charts convert a dose you already have into syringe units — they are reconstitution math, not a dosing recommendation. The dose figures shown are commonly cited examples for context only, not medical advice. Always follow qualified guidance for what to actually take.' },
  { q: 'My vial size or dose is different — what do I do?', a: 'Open the peptide calculator and enter your exact vial amount, water, and dose. The charts cover the most common vial sizes; the calculator handles any combination and shows the units, concentration, and doses per vial for your specific setup.' },
]

export default function Charts() {
  const { getBySlug } = usePeptides()
  const peps = CHART_SLUGS.map((s) => getBySlug(s)).filter(Boolean)

  return (
    <main className="page">
      <Seo
        title="Peptide Dosing Charts — Units per Vial | CalculateMyPeptides"
        description="Per-peptide dosing charts: how many insulin-syringe units to draw for common doses and bacteriostatic water amounts. BPC-157, TB-500, semaglutide, tirzepatide, and more."
        image="/og/chart.png"
        jsonLd={faqLd('Peptide dosing charts — FAQ', CHART_FAQ)}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Dosing Charts', path: '/charts' },
        ]}
      />
      <div className="container">
        <span className="pill blue" style={{ marginBottom: 10 }}>Reference</span>
        <h1 className="section-title">Peptide Dosing Charts</h1>
        <p className="section-sub" style={{ maxWidth: '62ch' }}>
          Reconstitution charts for popular peptides — how many units to draw on a U-100 insulin
          syringe for common doses and water amounts. Every chart is backed by our{' '}
          <Link to="/calculator">peptide calculator</Link>.
        </p>
        <LastUpdated />

        <div className="chart-index-grid">
          {peps.map((p) => (
            <Link key={p.slug} to={`/charts/${p.slug}`} className="chart-index-card">
              <span className="chart-index-name">{p.name}</span>
              {p.calcPreset && (
                <span className="chart-index-meta">{p.calcPreset.vialMg} mg vial · {p.category}</span>
              )}
            </Link>
          ))}
        </div>

        <section className="page-narrow" style={{ margin: '36px auto 0' }}>
          <h2 className="pep-h2">How to use these charts</h2>
          <p>
            Reconstituting a peptide comes down to one relationship: the amount of peptide in the vial, the
            amount of bacteriostatic water you add, and the dose you want. Together those set the concentration,
            and the concentration sets how many syringe units each dose works out to. A dosing chart pre-computes
            that grid for a given peptide so you don’t have to run the numbers every time.
          </p>
          <p>
            Every chart on this page uses a U-100 insulin syringe (100 units = 1 mL) and the same formula as our{' '}
            <Link to="/calculator">peptide calculator</Link>. Pick a peptide below to see its full table, or read
            the <Link to="/peptide-dosage-guide">dosage guide</Link> for a worked example. If your vial size or
            dose isn’t on a chart, the calculator handles any combination.
          </p>
          <p>
            Prefer a quick-reference chart by vial size? The <Link to="/peptide-dosage-chart">peptide dosage chart</Link>{' '}
            gives printable, at-a-glance units to draw for 5–60 mg vials.
          </p>

          <FaqBlock title="Dosing chart FAQ" items={CHART_FAQ} />
        </section>
      </div>
    </main>
  )
}
